import { useEffect, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { ProjectTask } from "../types";
import { useLanguage } from "../contexts/LanguageContext";

interface CompleteTaskConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: ProjectTask | null;
  onComplete: (task: ProjectTask) => void;
}

export default function CompleteTaskConfirmDialog({
  open,
  onOpenChange,
  task,
  onComplete,
}: CompleteTaskConfirmDialogProps) {
  // Early return before hooks if task is null
  if (!task) return null;

  const { t } = useLanguage();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const handleComplete = () => {
    onComplete(task);
    onOpenChange(false);
  };

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent ref={dialogContentRef}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('tasks.completeTaskConfirm')}</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block mb-2">
              {t('tasks.completeTaskDesc')}
            </span>
            <span className="block px-3 py-2 bg-gray-50 rounded border border-gray-200 text-gray-900">
              {task.name}
            </span>
            <span className="block mt-3 text-sm text-gray-600">
              {t('tasks.completeTaskNote')}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleComplete}>
            {t('tasks.complete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

